import type { ReactNode } from 'react';
import { Flex, theme, Typography } from 'antd';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { Button } from '../Button';
import { IconChip } from '../IconChip';
import { radius } from '../../theme/radius';

export interface LaunchRowProps {
  /** Icon element; the chip sizes it, so pass it without a size of its own. */
  icon: ReactNode;
  /** Accent as a 6-digit hex — tints the chip and washes the row behind it. */
  color: string;
  title: string;
  description: string;
  actionLabel: string;
  onAction?: () => void;
  /** Disables the action, e.g. while its destination is still resolving. */
  disabled?: boolean;
}

/**
 * A way out of the current page: what's on the other side on the left, the
 * button that opens it on the right.
 */
export const LaunchRow = ({
  icon,
  color,
  title,
  description,
  actionLabel,
  onAction,
  disabled,
}: LaunchRowProps) => {
  const { token } = theme.useToken();

  return (
    <Flex
      align="center"
      gap={16}
      wrap
      style={{
        padding: 16,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: radius.box,
        // Strongest behind the chip, gone by the midpoint.
        background: `linear-gradient(to right, ${color}0f 0%, ${color}00 50%)`,
      }}
    >
      <IconChip color={color} size="m" icon={icon} />
      <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
        <Typography.Text strong>{title}</Typography.Text>
        <Typography.Text type="secondary">{description}</Typography.Text>
      </Flex>
      <Button
        variant="primary"
        size="m"
        rightIcon={<ArrowSquareOutIcon size={16} />}
        disabled={disabled}
        onClick={onAction}
      >
        {actionLabel}
      </Button>
    </Flex>
  );
};
